import { useFormikContext } from "collections/Form";
import { ModalConfirm, useModalDispatch } from "collections/Modal";
import {
  CUSTOM_SUPPORT_UPDATE_PATH,
  DEVICE_PARAM_TYPE,
  MAIN,
  MY_SETTINGS_CHANGES_VIEW,
  MY_SETTINGS_DEVICE_CONFIG_VIEW,
  MY_SETTINGS_POLICY_VIEW
} from "Constants";
import {
  CategoryDiv,
  CategorySwitcher,
  ToolTip,
  useFeatureFlagWithTenant
} from "elements";
import {
  DeviceConfigParamFragment,
  DeviceFragment,
  Maybe,
  PolicyDeploymentScope
} from "generated/graphql";
import { LocationDescriptorObject } from "history";
import {
  Link,
  parse,
  stringify,
  useHistory,
  useQueryParamString
} from "polyrouter";
import React, { useContext, useEffect } from "react";
import { useTranslation } from "utilities/i18n";
import { useDispatch, useSelector } from "react-redux";
import { State } from "store";
import { settingChanged, SettingsChanges } from "store/settings";
import { ProtectedRoute } from "TsRoleGuards";
import { useInheritanceAndIdentifier } from "../Settings-Desktop/hooks";
import { SectionSetting } from "./ProvisionedDeviceSettings";
import {
  Setting,
  SettingsTree,
  SettingValue,
  useProvisionedDevicePolicyScopeContext
} from "./ProvisionedDeviceSettingsRouter";
import { SW_UPDATE_POLICY_SETTINGS } from "./ProvisionedUpdateSoftware";
import "./MySettings.scss";
import { SettingsMapper } from "./SettingsMapper";
import { deviceSupportsExportConfig } from "utilities/modelSelectors";
import { handleNullFields } from "utilities/stringUtils";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";
import { useDeviceConfig } from "./useDeviceConfig";
import { usePermissions } from "rbac";
import { SwSupportUpdatePathFragment } from "generated/graphql";

export enum RevertAction {
  REVERT = "REVERT",
  DISCARD = "DISCARD"
}

export const getUnsavedChangesCount = (settings: SettingsChanges): number => {
  let count: number = 0;

  // This goes through the changed settings tree to flatten out the settings in each category.
  Object.values(settings).forEach(categorySettings => {
    Object.values(categorySettings).forEach(subcategorySettings => {
      Object.values(subcategorySettings).forEach(collectionSettings => {
        count += Object.keys(collectionSettings).length;
      });
    });
  });

  return count;
};

export function useRevertSetting() {
  const { setFieldValue, getFieldMeta } = useFormikContext();
  const dispatch = useDispatch();

  return (
    category: string,
    subcategory: string,
    collection: string,
    settingName: string,
    type?: string
  ) => {
    // update in redux as reverted
    dispatch(
      settingChanged({
        category,
        subcategory,
        collection,
        settingName,
        change: "reverted"
      })
    );

    const fieldName = `['${settingName}']`;
    let value: SettingValue = "";
    if (type === DEVICE_PARAM_TYPE.BOOL || type === DEVICE_PARAM_TYPE.XBOOL) {
      value = false;
    } else if (settingName === CUSTOM_SUPPORT_UPDATE_PATH) {
      value =
        (getFieldMeta(fieldName)
          .initialValue as SwSupportUpdatePathFragment[])?.map(
          ({ deviceModel }) => ({
            deviceModel,
            swPath: ""
          })
        ) ?? [];
    }

    // wrap in setTimeout to allow revert change to propagate to state (so it is ignored during validation)
    setTimeout(() => {
      // remove the value (set it to "" or to false in case of bool settings)
      setFieldValue(fieldName, value);
    });
  };
}

export function useDiscardSetting() {
  const { setFieldValue, getFieldMeta } = useFormikContext();
  const dispatch = useDispatch();

  return (
    category: string,
    subcategory: string,
    collection: string,
    settingName: string
  ) => {
    // clear it from redux
    dispatch(
      settingChanged({
        category,
        subcategory,
        collection,
        settingName,
        change: ""
      })
    );

    // set it back to its original value
    const fieldName = `['${settingName}']`;
    const initialValue = getFieldMeta(fieldName).initialValue;
    setFieldValue(fieldName, initialValue);
  };
}

export const DEFAULT_GROUP = "DEFAULT";

export type ConfigParam = DeviceConfigParamFragment;

export type Settings<T> = {
  [category: string]: CategorySettings<T>;
};

export type CategorySettings<T> = {
  [subcategory: string]: SubcategorySettings<T>;
};

export type SubcategorySettings<T> = {
  [collection: string]: {
    [grouping: string]: T[];
  };
};

export type MySettingsProps = {
  deviceSettings: SettingsTree;
  updateSettings: Record<string, SectionSetting>;
  device: DeviceFragment | undefined;
  deviceModel: string;
  site?: string;
  dirty: boolean;
  group?: string;
  userGroup?: string;
};

const addSettings = <T extends Setting | ConfigParam>(
  allSettings: Settings<T>,
  settings: T[],
  category: string,
  subcategory: string,
  collection: string,
  group: string = DEFAULT_GROUP
) => {
  const path = [category, subcategory, collection, group];
  const length = path.length;
  var current: any = allSettings;
  // we are going to loop through path and create object structure if it doesn't exist
  // adding the settings at the end of the path
  path.forEach((key, index) => {
    if (!current[key]) {
      // if last item in the path set it to empty array, otherwise to empty object
      current[key] = index === length - 1 ? [] : {};
    }
    current = current[key];
  });

  current.push(...settings);
};

const getPolicySettings = (
  settings: SettingsTree,
  updateSettings: Record<string, SectionSetting>,
  unsavedChanges: SettingsChanges,
  currentPolicyScope: PolicyDeploymentScope | null
) => {
  return parseSettings(
    settings,
    updateSettings,
    (
      setting: Setting,
      category: string,
      subcategory: string,
      collection: string
    ) =>
      setting.policyDeploymentScope !== null &&
      (setting.policyDeploymentScope === currentPolicyScope ||
        currentPolicyScope === PolicyDeploymentScope.Device) &&
      unsavedChanges[category]?.[subcategory]?.[collection]?.[
        setting.settingName
      ] !== "reverted"
  );
};

const getChangedSettings = (
  settings: SettingsTree,
  updateSettings: Record<string, SectionSetting>,
  unsavedChanges: SettingsChanges
) =>
  parseSettings(
    settings,
    updateSettings,
    (
      setting: Setting,
      category: string,
      subcategory: string,
      collection: string
    ) =>
      !!unsavedChanges[category]?.[subcategory]?.[collection]?.[
        setting.settingName
      ]
  );

const getDeviceConfig = (configParams: Maybe<Maybe<ConfigParam>[]>) => {
  const deviceConfig: Settings<ConfigParam> = {};
  configParams?.forEach(param => {
    const [category, subcategory] = param?.categories ?? [];
    if (param) {
      addSettings(
        deviceConfig,
        [param],
        handleNullFields(category),
        handleNullFields(subcategory),
        MAIN
      );
    }
  });

  return deviceConfig;
};

const parseSettings = (
  settings: SettingsTree,
  updateSettings: Record<string, SectionSetting>,
  filterFn: (
    setting: Setting,
    category: string,
    subcategory: string,
    collection: string
  ) => boolean
) => {
  const deviceSettings: Settings<Setting> = {};
  Object.entries(settings).forEach(([category, settingsBySubcategory]) => {
    Object.entries(settingsBySubcategory).forEach(
      ([subcategory, settingsByCollection]) => {
        Object.entries(settingsByCollection).forEach(
          ([collection, settingsByGroup]) => {
            Object.entries(settingsByGroup).forEach(([group, settings]) => {
              if (Array.isArray(settings)) {
                settings = settings.filter(setting =>
                  filterFn(setting, category, subcategory, collection)
                );
                if (settings.length) {
                  addSettings(
                    deviceSettings,
                    settings,
                    category,
                    subcategory,
                    collection
                  );
                }
              } else {
                Object.values(settings).forEach(settingsArray => {
                  settingsArray = settingsArray.filter(setting =>
                    filterFn(setting, category, subcategory, collection)
                  );
                  if (settingsArray.length) {
                    addSettings(
                      deviceSettings,
                      settingsArray,
                      category,
                      subcategory,
                      collection,
                      group
                    );
                  }
                });
              }
            });
          }
        );
      }
    );
  });

  const swUpdateSettings = Object.entries(updateSettings)
    .filter(([_, sett]) =>
      filterFn(sett, sett.category, sett.subcategory, sett.collection)
    )
    .reduce((result, [name, sett]) => {
      result[name] = sett;
      return result;
    }, {} as Record<string, SectionSetting>);

  return [deviceSettings, swUpdateSettings] as const;
};

const MySettings: React.FC<MySettingsProps> = props => {
  const { t } = useTranslation();
  const hasPermission = usePermissions();
  const [tab, setTab] = useQueryParamString<"view">("view");
  const unsavedChanges = useSelector((s: State) => s.settings);
  const unsavedChangesCount = getUnsavedChangesCount(unsavedChanges);
  const [{ policyScope }] = useProvisionedDevicePolicyScopeContext();
  const [isDeviceConfigEnabled] = useFeatureFlagWithTenant(
    "DEV_CONFIG_OVERRIDES"
  );
  const canViewDeviceConfig = hasPermission("set:device:config");
  const tenantId = useContext(SelectedTenantContext).tenant?.id ?? "";

  const { deviceModel, device, deviceSettings, updateSettings } = props;
  const supportsDeviceConfig =
    deviceSupportsExportConfig(deviceModel, device?.softwareVersion) &&
    isDeviceConfigEnabled;
  const isOnline = !!device?.connected;
  const showDeviceConfig = canViewDeviceConfig && supportsDeviceConfig;

  const { deviceConfig, fetching } = useDeviceConfig({
    deviceId: device?.id ?? "",
    deviceModel: deviceModel,
    fetchDeviceConfig: showDeviceConfig && isOnline,
    tenantId: tenantId
  });

  useEffect(() => {
    // Redirect to This Policy tab in the following scenarios:
    // 1. Selected tab is not one of the expected values
    // 2. Changes tab is selected but there are no changes (tab selector won't be displayed in this case)
    // 3. This Device tab is selected but Device Config is not supported for that device/user doesn't have necessary permissions, or device is not online
    if (
      (tab !== MY_SETTINGS_POLICY_VIEW &&
        tab !== MY_SETTINGS_DEVICE_CONFIG_VIEW &&
        tab !== MY_SETTINGS_CHANGES_VIEW) ||
      (tab === MY_SETTINGS_CHANGES_VIEW && unsavedChangesCount === 0) ||
      (tab === MY_SETTINGS_DEVICE_CONFIG_VIEW &&
        (!showDeviceConfig || !isOnline))
    ) {
      setTab(MY_SETTINGS_POLICY_VIEW);
    }
  }, [tab, setTab, unsavedChangesCount, isOnline, showDeviceConfig]);

  // depending on the chosen view show policy settings, changed settings, or device config
  let settingsToDisplay: Settings<Setting | ConfigParam> = {};
  let swUpdateSettingsToDisplay: Record<string, SectionSetting> = {};

  switch (tab) {
    case MY_SETTINGS_POLICY_VIEW:
      [settingsToDisplay, swUpdateSettingsToDisplay] = getPolicySettings(
        deviceSettings,
        updateSettings,
        unsavedChanges,
        policyScope
      );
      break;
    case MY_SETTINGS_CHANGES_VIEW:
      [settingsToDisplay, swUpdateSettingsToDisplay] = getChangedSettings(
        deviceSettings,
        updateSettings,
        unsavedChanges
      );
      break;
    case MY_SETTINGS_DEVICE_CONFIG_VIEW:
      settingsToDisplay = getDeviceConfig(deviceConfig);
      break;
  }

  const { policyIdentifier, inheritedBy } = useInheritanceAndIdentifier(
    policyScope,
    props
  );

  return (
    <>
      <div className="my-settings">
        <CategorySwitcher>
          <CategoryDiv
            label={t("THIS_POLICY")}
            active={tab === MY_SETTINGS_POLICY_VIEW}
            onClick={() => setTab(MY_SETTINGS_POLICY_VIEW)}
            data-testid="policy-switcher"
            divClass="pl-15 pr-15"
          />
          {showDeviceConfig && (
            <ToolTip
              iconTitle="info"
              iconReplacement={
                <CategoryDiv
                  label={t("THIS_DEVICE")}
                  active={tab === MY_SETTINGS_DEVICE_CONFIG_VIEW}
                  onClick={() => setTab(MY_SETTINGS_DEVICE_CONFIG_VIEW)}
                  data-testid="device-config-switcher"
                  divClass="pl-15 pr-15"
                  disabled={!isOnline}
                />
              }
              position="right"
              disablePopup={isOnline}
            >
              {t("DEVICE_CONFIG_DISABLED_DEVICE_OFFLINE")}
            </ToolTip>
          )}
          {unsavedChangesCount > 0 && (
            <CategoryDiv
              label={t("CHANGES")}
              active={tab === MY_SETTINGS_CHANGES_VIEW}
              onClick={() => setTab(MY_SETTINGS_CHANGES_VIEW)}
              data-testid="changes-switcher"
              divClass="pl-15 pr-15"
            />
          )}
        </CategorySwitcher>
        {unsavedChangesCount > 0 && (
          <span className="inline-block unsaved-changes-ellipse">
            {unsavedChangesCount}
          </span>
        )}
        <SettingsMapper
          {...props}
          view={tab ?? MY_SETTINGS_POLICY_VIEW}
          inheritedBy={inheritedBy}
          settingsToDisplay={settingsToDisplay}
          swUpdateSettingsToDisplay={swUpdateSettingsToDisplay}
          loading={tab === MY_SETTINGS_DEVICE_CONFIG_VIEW && fetching}
        />
      </div>
      {tab === MY_SETTINGS_POLICY_VIEW && (
        <ProtectedRoute nest exact path="/remove" requirement="update:device">
          <RemoveAllSettingsModal
            settings={settingsToDisplay as Settings<Setting>}
            swUpdateSettings={swUpdateSettingsToDisplay}
            policyIdentifier={policyIdentifier}
            deploymentScope={policyScope}
          />
        </ProtectedRoute>
      )}
    </>
  );
};

export const UnsavedChangesReviewMessage: React.FC<{
  unsavedChanges: number;
  deviceId?: string;
}> = ({ unsavedChanges, deviceId }) => {
  const { t } = useTranslation();
  const history = useHistory();

  if (unsavedChanges <= 0) {
    return null;
  }

  const MY_SETTINGS_PATH = "settings/my-settings";
  const pathnameUrl = deviceId
    ? `/manage/inventory/details/${deviceId}/${MY_SETTINGS_PATH}`
    : `/manage/policy/details/${MY_SETTINGS_PATH}`;

  const queryParams: Record<"view", string> = {
    view: MY_SETTINGS_CHANGES_VIEW
  };
  const reviewUrl: LocationDescriptorObject = {
    pathname: pathnameUrl,
    search: stringify({
      ...parse(history.location.search),
      ...queryParams
    })
  };

  return (
    <p>
      <span className="review-changes-dialogue">
        {t("REVIEW_CHANGES_DIALOGUE", {
          unsavedChanges
        })}
      </span>
      <Link to={reviewUrl}>{t("REVIEW_CHANGES")}</Link>
    </p>
  );
};

export const RemoveAllSettingsModal: React.FC<{
  settings: Settings<Setting>;
  swUpdateSettings: Record<string, SectionSetting>;
  policyIdentifier: string;
  deploymentScope: PolicyDeploymentScope | null;
}> = ({ settings, swUpdateSettings, policyIdentifier, deploymentScope }) => {
  const { t } = useTranslation();
  const dispatchEvent = useModalDispatch();
  const revertSetting = useRevertSetting();

  const removeAll = () => {
    Object.entries(settings).forEach(([category, categorySettings]) => {
      Object.entries(categorySettings).forEach(
        ([subcategory, subcategorySettings]) => {
          Object.entries(subcategorySettings).forEach(
            ([collection, collectionSettings]) => {
              Object.values(collectionSettings)
                .flat()
                .forEach(setting => {
                  if (setting.policyDeploymentScope === deploymentScope) {
                    revertSetting(
                      category,
                      subcategory,
                      collection,
                      setting.settingName,
                      setting.type ?? ""
                    );
                  }
                });
            }
          );
        }
      );
    });

    Object.values(swUpdateSettings).forEach(
      ({
        category,
        subcategory,
        collection,
        settingName,
        policyDeploymentScope
      }) => {
        // setting should be reverted if its coming from same scope as current policy deployment scope,
        // and its not one of SW Update policy settings coming from Device Model scope (that SW Update policy should always exist)
        if (
          policyDeploymentScope === deploymentScope &&
          (deploymentScope !== PolicyDeploymentScope.DeviceModel ||
            !SW_UPDATE_POLICY_SETTINGS.includes(settingName))
        ) {
          revertSetting(category, subcategory, collection, settingName);
        }
      }
    );

    dispatchEvent();
  };

  return (
    <ModalConfirm
      title={t("REMOVE_ALL_POLICY_SETTINGS")}
      onConfirm={removeAll}
      onCancel={dispatchEvent}
      open={true}
      message={t("REMOVE_ALL_POLICY_SETTINGS_CONFIRMATION", {
        name: policyIdentifier
      })}
      confirmLabel={t("OK")}
    />
  );
};

export default MySettings;
